/** TodoAttachments.tsx — 할 일 첨부파일 영역 (업로드/다운로드/삭제) */
import { useState, useRef } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { TodoAttachment } from '../types';

interface Props {
  todoId: string;
  attachments: TodoAttachment[];
  onChange: (list: TodoAttachment[]) => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

function fileIcon(mime: string): string {
  if (mime.startsWith('image/')) return '🖼';
  if (mime.includes('pdf')) return '📄';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  if (mime.includes('sheet') || mime.includes('excel')) return '📊';
  if (mime.includes('presentation') || mime.includes('powerpoint')) return '📽';
  if (mime.includes('zip') || mime.includes('compressed')) return '🗜';
  return '📎';
}

export default function TodoAttachments({ todoId, attachments, onChange, onToast }: Props) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  async function uploadOne(file: File) {
    setUploading(true);
    try {
      const r = await todosApi.uploadAttachment(todoId, file);
      if (r.success && r.data) {
        onChange([...attachments, r.data]);
        onToast?.(`${file.name} 업로드됨`, 'success');
      } else onToast?.(r.error || '업로드 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '업로드 실패', 'error'); }
    finally { setUploading(false); }
  }

  async function handleFiles(files: FileList | null) {
    if (!files) return;
    for (let i = 0; i < files.length; i++) {
      await uploadOne(files[i]);
    }
    if (fileRef.current) fileRef.current.value = '';
  }

  async function download(att: TodoAttachment) {
    try {
      const r = await todosApi.getAttachmentUrl(todoId, att.id);
      if (r.success && r.data?.url) window.open(r.data.url, '_blank');
      else onToast?.(r.error || '다운로드 URL 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '다운로드 실패', 'error'); }
  }

  async function remove(att: TodoAttachment) {
    if (!window.confirm(`"${att.fileName}" 삭제하시겠습니까?`)) return;
    try {
      const r = await todosApi.deleteAttachment(todoId, att.id);
      if (r.success) {
        onChange(attachments.filter(a => a.id !== att.id));
        onToast?.('삭제됨', 'success');
      } else onToast?.(r.error || '삭제 실패', 'error');
    } catch (e) { onToast?.(e instanceof Error ? e.message : '삭제 실패', 'error'); }
  }

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
        style={{
          border: `1px dashed ${dragOver ? C.accent : C.border}`,
          background: dragOver ? C.accentLt : C.white,
          borderRadius: R.sm, padding: '6px 10px',
          textAlign: 'center', cursor: 'pointer', marginBottom: attachments.length > 0 ? 5 : 0,
          transition: 'border-color 0.2s, background 0.2s',
        }}
        onClick={() => fileRef.current?.click()}
      >
        <div style={{ fontSize: T.xs, color: dragOver ? C.accent : C.muted, fontWeight: 500 }}>
          {uploading ? '⏳ 업로드 중...' : '📎 파일 추가 (클릭 또는 드래그, 최대 50MB)'}
        </div>
        <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)} />
      </div>

      {attachments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {attachments.map(att => (
            <div key={att.id} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px', background: C.white,
              border: `1px solid ${C.border}`, borderRadius: R.sm,
            }}>
              <span style={{ fontSize: T.lg }}>{fileIcon(att.mimeType)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: T.sm, color: C.text, fontWeight: 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={att.fileName}>
                  {att.fileName}
                </div>
                <div style={{ fontSize: T.xs, color: C.muted }}>
                  {formatSize(att.size)}
                </div>
              </div>
              <button onClick={() => download(att)}
                style={{ padding: '4px 10px', borderRadius: R.sm,
                  border: `1px solid ${C.accent}`, background: C.white,
                  color: C.accent, fontSize: T.xs, fontWeight: 600, cursor: 'pointer' }}>
                ⬇ 다운로드
              </button>
              <button onClick={() => remove(att)}
                style={{ padding: '4px 8px', borderRadius: R.sm,
                  border: `1px solid ${C.border}`, background: C.white,
                  color: C.red, fontSize: T.xs, cursor: 'pointer' }}>
                🗑
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
